import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Calendar, Check, Clock, Upload, MapPin } from "lucide-react";

const DATES = [
  { d: "20 September 2026", w: "Sunday" },
];
const VENUE = "Makrana";
const TIMES = [
  { value: "8:00 AM", label: "8:00 AM – Morning" },
  { value: "1:00 PM", label: "1:00 PM – Afternoon" },
];
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

export default function TrialSlotModal({ open, onClose }) {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState("");
  const [timeSlot, setTimeSlot] = useState("");
  const [form, setForm] = useState({ name: "", mobile: "" });
  const [screenshotDataUrl, setScreenshotDataUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const close = () => {
    setStep(1); setDate(""); setTimeSlot(""); setForm({ name: "", mobile: "" }); setScreenshotDataUrl(""); setSubmitting(false); setDone(false);
    onClose();
  };

  const pickSlot = (d, t) => { setDate(d); setTimeSlot(t); setStep(2); };

  const readScreenshot = (file) => {
    if (!file) return;
    if (!ALLOWED_TYPES.includes((file.type || "").toLowerCase())) {
      toast.error("Only JPG, JPEG, PNG or WEBP are allowed"); return;
    }
    if (file.size > 2_000_000) { toast.error("Screenshot must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setScreenshotDataUrl(String(reader.result || ""));
    reader.onerror = () => toast.error("Could not read file");
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.name || !form.mobile) { toast.error("Please fill all fields"); return; }
    setSubmitting(true);
    try {
      await api.post("/trial-slot-bookings", { ...form, trial_date: date, time_slot: timeSlot, screenshot_data_url: screenshotDataUrl });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Booking failed");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-4" onClick={close} data-testid="trial-slot-modal">
      <div className="bg-[#0B132B] border border-white/15 max-w-md w-full p-6 rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#39FF14]">Book Trial Slot</div>
            <h2 className="font-display text-2xl mt-1">{done ? "Booking Confirmed" : step === 1 ? "Select Date & Time" : "Your Details"}</h2>
          </div>
          <button onClick={close} className="text-white/60 hover:text-white"><X /></button>
        </div>

        {done ? (
          <div className="mt-6 text-center">
            <div className="w-14 h-14 rounded-full bg-[#39FF14] text-black grid place-items-center mx-auto"><Check size={28} /></div>
            <p className="mt-4 text-white/80" data-testid="trial-slot-success">Your trial slot has been booked successfully. Our team will contact you soon.</p>
            <button onClick={close} className="mt-6 px-6 py-2 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-xs">Close</button>
          </div>
        ) : step === 1 ? (
          <div className="mt-5 space-y-3">
            {DATES.map((x) => (
              <div key={x.d} className="p-3 border border-white/15 bg-black/30" data-testid={`trial-date-${x.d}`}>
                <div className="flex items-center gap-2">
                  <Calendar className="text-[#39FF14]" size={16} />
                  <div className="font-display text-lg">{x.d}</div>
                  <div className="text-xs text-white/60">• {x.w}</div>
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-white/60">
                  <MapPin size={12} className="text-[#FFD700]" />{VENUE}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {TIMES.map((t) => (
                    <button key={t.value} data-testid={`trial-slot-${x.d}-${t.value}`} onClick={() => pickSlot(x.d, t.value)}
                      className="flex items-center gap-2 justify-center py-2 border border-white/20 hover:border-[#39FF14] hover:bg-[#39FF14]/10 text-xs">
                      <Clock size={14} className="text-[#FFD700]" />{t.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            <div className="text-xs uppercase tracking-widest text-[#FFD700]">Selected: {date} • {timeSlot} • {VENUE}</div>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/60">Player Name</span>
              <input data-testid="ts-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 w-full bg-black/50 border border-white/15 focus:border-[#39FF14] focus:outline-none px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/60">Mobile Number</span>
              <input data-testid="ts-mobile" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="mt-2 w-full bg-black/50 border border-white/15 focus:border-[#39FF14] focus:outline-none px-3 py-2 text-sm" placeholder="10-digit mobile" />
            </label>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Golden Ticket / Registration Payment Screenshot</div>
              <div className="flex items-center gap-3">
                {screenshotDataUrl ? <img src={screenshotDataUrl} alt="screenshot preview" className="w-16 h-16 object-cover rounded border border-white/10"/> : null}
                <label className="flex-1 flex items-center gap-2 border border-dashed border-white/15 px-3 py-3 cursor-pointer text-xs hover:border-[#39FF14]">
                  <Upload size={14} className="text-[#39FF14]"/>
                  <span>{screenshotDataUrl ? "Replace screenshot" : "Choose screenshot (JPG, JPEG, PNG, WEBP)"}</span>
                  <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => readScreenshot(e.target.files?.[0])} data-testid="ts-screenshot"/>
                </label>
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setStep(1)} className="px-4 py-3 border border-white/20 text-xs uppercase tracking-widest">Back</button>
              <button data-testid="ts-submit" onClick={submit} disabled={submitting} className="flex-1 py-3 bg-[#39FF14] text-black font-bold uppercase tracking-widest disabled:opacity-50">{submitting ? "Booking…" : "Confirm Slot"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
